/* Venice add-on: native parent/child cart lines plus storefront reconciliation.
 * The separate Shopify checkout validation is REQUIRED for server enforcement.
 */
(function (scope) {
  'use strict';

  function inspectCart(cart, mainId, extraId) {
    if (!cart || !Array.isArray(cart.items)) {
      throw new Error('Invalid cart response.');
    }

    const main = cart.items.filter(
      item => String(item.product_id) === String(mainId)
    );

    const extras = cart.items.filter(
      item => String(item.product_id) === String(extraId)
    );

    const mainQuantity = main.reduce(
      (sum, item) => sum + Number(item.quantity),
      0
    );

    const extraQuantity = extras.reduce(
      (sum, item) => sum + Number(item.quantity),
      0
    );

    const mainKeys = new Set(main.map(item => item.key));

    return {
      main,
      extras,
      mainKeys,
      mainQuantity,
      extraQuantity,
      canOffer: mainQuantity === 1 && extraQuantity === 0
    };
  }

  function getRepairs(cart, mainId, extraId) {
    const state = inspectCart(cart, mainId, extraId);
    const updates = {};

    let remaining = state.mainQuantity === 1 ? 1 : 0;

    for (const item of state.extras) {
      const linked = state.mainKeys.has(
        item.parent_relationship?.parent_key
      );

      const keep = linked
        ? Math.min(Number(item.quantity), remaining)
        : 0;

      remaining -= keep;

      if (keep !== Number(item.quantity)) {
        if (!item.key) {
          throw new Error('Could not identify the add-on cart line.');
        }

        updates[item.key] = keep;
      }
    }

    return updates;
  }

  function createService({
    mainId,
    extraId,
    request,
    onCart = function () {}
  }) {
    if (
      !/^[1-9]\d*$/.test(String(mainId)) ||
      !/^[1-9]\d*$/.test(String(extraId)) ||
      String(mainId) === String(extraId)
    ) {
      throw new Error('Select two different products for the add-on rule.');
    }

    let tail = Promise.resolve();
    let lastCart = null;

    function enqueue(task) {
      const job = tail.then(task, task);
      tail = job.catch(function () {});
      return job;
    }

    function publish(cart, changed) {
      lastCart = cart;
      onCart(cart, { changed });
      return cart;
    }

    async function repairInternal() {
      let changed = false;

      for (let attempt = 0; attempt < 4; attempt++) {
        const cart = await request('cart.js');
        const updates = getRepairs(cart, mainId, extraId);

        if (Object.keys(updates).length === 0) {
          return publish(cart, changed);
        }

        if (attempt === 3) {
          publish(cart, changed);

          throw new Error(
            'Your cart is still changing. Please wait and try again.'
          );
        }

        await request('cart/update.js', { updates });
        changed = true;
      }
    }

    async function addInternal(variantId) {
      if (!/^[1-9]\d*$/.test(String(variantId))) {
        throw new Error('Choose an available color.');
      }

      let cart = await repairInternal();
      const state = inspectCart(cart, mainId, extraId);

      if (!state.canOffer) {
        throw new Error(
          'The extra is available with exactly one main Venice and no other extra.'
        );
      }

      const parent = state.main[0];

      if (
        !parent.key ||
        parent.parent_relationship ||
        parent.item_components?.length
      ) {
        throw new Error(
          'This main item does not support the add-on. Please contact us for help.'
        );
      }

      await request('cart/add.js', {
        items: [
          {
            id: String(variantId),
            quantity: 1,
            parent_line_key: parent.key
          }
        ]
      });

      cart = await request('cart.js');

      const after = inspectCart(cart, mainId, extraId);

      const added = after.extras.find(
        item =>
          String(item.variant_id || item.id) === String(variantId) &&
          after.mainKeys.has(item.parent_relationship?.parent_key)
      );

      const valid =
        !!added &&
        after.mainQuantity === 1 &&
        after.extraQuantity === 1;

      if (!valid) {
        await repairInternal();

        throw new Error(
          'The extra could not be linked to the main Venice. Please check your cart and try again.'
        );
      }

      return publish(cart, true);
    }

    return {
      reconcile: () => enqueue(repairInternal),
      add: variantId => enqueue(() => addInternal(variantId)),

      get cart() {
        return lastCart;
      }
    };
  }

  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      inspectCart,
      getRepairs,
      createService
    };
  }

  if (!scope.document || scope.__veniceUpsellGuard) return;

  const document = scope.document;

  const guard = {
    service: null,
    config: null,
    busy: false,
    checkoutBusy: false,
    message: '',
    scheduled: null
  };

  scope.__veniceUpsellGuard = guard;

  let lastSignature = '';

  function showMessage(message) {
    guard.message = message;

    document
      .querySelectorAll('[data-venice-upsell-status]')
      .forEach(node => {
        node.textContent = message;
        node.hidden = !message;
      });
  }

  function syncButtons() {
    let eligible = false;

    if (guard.service?.cart && guard.config) {
      eligible = inspectCart(
        guard.service.cart,
        guard.config.mainId,
        guard.config.extraId
      ).canOffer;
    }

    document
      .querySelectorAll('[data-venice-extra-add]')
      .forEach(button => {
        const card = button.closest('[data-venice-extra-card]');

        const correctProduct =
          card?.dataset.extraProductId === guard.config?.extraId;

        button.disabled =
          !eligible || !correctProduct || guard.busy || guard.checkoutBusy;

        button.setAttribute('aria-busy', String(guard.busy || guard.checkoutBusy));
      });

    showMessage(guard.message);
  }

  function refreshTheme() {
    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true
      })
    );
  }

  function closeEmptyDrawer(cart) {
    // Solo cierra si no queda ningún artículo.
    if (!Array.isArray(cart?.items) || cart.items.length !== 0) {
      return;
    }

    const drawer = document.getElementById('cart-drawer');

    // Usa el cierre nativo del tema.
    if (typeof drawer?.hide === 'function') {
      drawer.hide();
    }
  }

  function broadcastChange() {
    try {
      scope.localStorage.setItem(
        'venice-upsell-cart-update',
        String(Date.now()) + ':' + Math.random()
      );
    } catch (_) {}
  }

  function fail(error) {
    console.error('[Venice add-on]', error);

    showMessage(
      error?.message ||
      'We could not check your cart. Please refresh and try again.'
    );
  }

  async function request(root, path, body) {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 15000);

    try {
      const response = await scope.fetch(root + path, {
        method: body ? 'POST' : 'GET',
        credentials: 'same-origin',
        cache: 'no-store',
        signal: controller.signal,

        headers: body
          ? {
              'Content-Type': 'application/json',
              Accept: 'application/json'
            }
          : {
              Accept: 'application/json'
            },

        body: body ? JSON.stringify(body) : undefined
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          typeof data.description === 'string'
            ? data.description
            : 'We could not update your cart. Please try again.'
        );
      }

      return data;
    } catch (error) {
      if (error.name === 'AbortError') {
        throw new Error(
          'The cart took too long to respond. Check your cart before trying again.'
        );
      }

      throw error;
    } finally {
      clearTimeout(timeout);
    }
  }

  function schedule() {
    clearTimeout(guard.scheduled);

    guard.scheduled = setTimeout(() => {
      if (!guard.service) return;

      guard.service
        .reconcile()
        .then(syncButtons)
        .catch(error => {
          syncButtons();
          fail(error);
        });
    }, 100);
  }

  function configure(node) {
    const mainId = node.dataset.mainId || '';
    const extraId = node.dataset.extraId || '';

    const rootUrl = new URL(
      scope.Shopify?.routes?.root || node.dataset.root || '/',
      scope.location.origin
    );

    if (rootUrl.origin !== scope.location.origin) return;

    const root = rootUrl.pathname.replace(/\/?$/, '/');

    if (!mainId || !extraId || mainId === extraId) {
      guard.config = null;
      guard.service = null;
      syncButtons();
      return;
    }

    if (
      guard.config?.mainId !== mainId ||
      guard.config?.extraId !== extraId ||
      guard.config?.root !== root
    ) {
      const config = {
        mainId,
        extraId,
        root
      };

      guard.config = config;
      lastSignature = '';

      guard.service = createService({
        mainId,
        extraId,

        request: (path, body) => request(root, path, body),

        onCart(cart, { changed }) {
          if (guard.config !== config) return;

          const signature = JSON.stringify(
            cart.items.map(item => [
              item.key,
              item.quantity,
              item.final_line_price,
              item.parent_relationship?.parent_key
            ])
          );

          const different =
            !!lastSignature && signature !== lastSignature;

          lastSignature = signature;

          if (changed) {
            showMessage('Your cart has been updated.');
          }

          syncButtons();

          if (changed || different) {
            refreshTheme();
            closeEmptyDrawer(cart);
          }

          if (changed) {
            broadcastChange();
          }
        }
      });
    }

    syncButtons();
    schedule();
  }

  if (!scope.customElements.get('venice-upsell-state')) {
    scope.customElements.define(
      'venice-upsell-state',
      class extends HTMLElement {
        connectedCallback() {
          configure(this);
        }
      }
    );
  }

  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;

      if (!(form instanceof HTMLFormElement)) return;

      const card = form.closest('[data-venice-extra-card]');

      if (!card) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (
        !guard.service ||
        card.dataset.extraProductId !== guard.config?.extraId
      ) {
        fail(
          new Error('The add-on is not ready. Please refresh the page.')
        );
        return;
      }

      if (guard.busy || guard.checkoutBusy) return;

      const field = form.elements.namedItem('id');

      if (
        !field ||
        field.disabled ||
        (
          field instanceof HTMLSelectElement &&
          field.selectedOptions[0]?.disabled
        )
      ) {
        fail(new Error('Choose an available color.'));
        return;
      }

      guard.busy = true;
      syncButtons();
      showMessage('');

      guard.service
        .add(String(field.value))
        .then(() => {
          showMessage('');

          const drawer = document.getElementById('cart-drawer');

          if (typeof drawer?.show === 'function') {
            drawer.show();
          }
        })
        .catch(error => {
          fail(error);
          schedule();
        })
        .finally(() => {
          guard.busy = false;
          syncButtons();
        });
    },
    true
  );

  // La validación en Shopify sigue siendo necesaria para proteger el checkout.
  const approvedForms = new WeakSet();

  function isCheckoutPath(url) {
    try {
      const parsed = new URL(url, scope.location.href);

      return (
        parsed.origin === scope.location.origin &&
        /\/checkout\/?$/.test(parsed.pathname)
      );
    } catch (_) {
      return false;
    }
  }

  function vipOwnsCheckout() {
    return scope.CartSkipVIP?.checkoutCoordinator === 'venice-v1';
  }

  // Mantener bloqueadas nuevas altas hasta que ambos controles terminen.
  // reconcile() espera las operaciones del upsell que ya estaban en curso.
  async function withCheckoutCheck(proceed) {
    if (guard.checkoutBusy) {
      throw new Error('Checkout is already being checked. Please wait.');
    }

    guard.checkoutBusy = true;
    showMessage('');
    syncButtons();

    try {
      if (guard.service) await guard.service.reconcile();
      return await proceed();
    } catch (error) {
      fail(error);
      throw error;
    } finally {
      guard.checkoutBusy = false;
      syncButtons();
    }
  }

  guard.withCheckoutCheck = withCheckoutCheck;

  async function beforeCheckout(proceed) {
    if (guard.checkoutBusy) return;

    try {
      await withCheckoutCheck(async () => {
        // Los formularios directos a /checkout siguen bajo este guard.
        if (scope.CartSkipVIP?.sync) await scope.CartSkipVIP.sync();
        return proceed();
      });
    } catch (_) {
      // withCheckoutCheck ya muestra el error y mantiene el checkout detenido.
    }
  }

  document.addEventListener(
    'submit',
    function (event) {
      const form = event.target;

      if (!(form instanceof HTMLFormElement) || !guard.service) {
        return;
      }

      if (approvedForms.delete(form)) return;

      const submitter = event.submitter;

      const cartForm = /\/cart\/?$/.test(
        new URL(form.action, scope.location.href).pathname
      );

      if (
        !isCheckoutPath(form.action) &&
        !(cartForm && submitter?.name === 'checkout')
      ) {
        return;
      }

      if (cartForm && submitter?.name === 'checkout' && vipOwnsCheckout()) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      if (cartForm && scope.CartSkipVIP && !vipOwnsCheckout()) {
        fail(new Error('Please refresh the page before checkout.'));
        return;
      }

      beforeCheckout(() => {
        if (
          form.isConnected &&
          (submitter?.isConnected || isCheckoutPath(form.action))
        ) {
          approvedForms.add(form);

          form.requestSubmit(
            submitter?.isConnected ? submitter : undefined
          );
        } else {
          scope.location.assign(guard.config.root + 'checkout');
        }
      });
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      const link =
        event.target instanceof Element
          ? event.target.closest('a[href]')
          : null;

      if (
        !link ||
        !guard.service ||
        !isCheckoutPath(link.href)
      ) {
        return;
      }

      if (
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return;
      }

      if (vipOwnsCheckout()) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (scope.CartSkipVIP && !vipOwnsCheckout()) {
        fail(new Error('Please refresh the page before checkout.'));
        return;
      }

      beforeCheckout(() => scope.location.assign(link.href));
    },
    true
  );

  document.addEventListener('cart:change', event => {
    closeEmptyDrawer(event.detail?.cart);
    schedule();
    broadcastChange();
  });

  scope.addEventListener('pageshow', schedule);
  scope.addEventListener('focus', schedule);

  scope.addEventListener('storage', event => {
    if (event.key === 'venice-upsell-cart-update') {
      schedule();
    }
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      schedule();
    }
  });

  document.addEventListener('shopify:section:load', schedule);
})(typeof window !== 'undefined' ? window : globalThis);